/**
 * Change-password form.
 *
 * Used both on the forced-first-login flow (must_change_password=1 redirects
 * here from the auth-guard layout) and as a voluntary password change. On
 * 200 the SPA stores the returned TokenPair, refetches /api/me, and
 * navigates back to / (or to the captured ?from referrer).
 */
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { BrandHeader } from "../components/BrandHeader";
import { loadTokens, saveTokens } from "../lib/auth-storage";
import { DOLMIT } from "../tokens";

function readFromParam(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("from");
  if (raw?.startsWith("/") && !raw.startsWith("//")) return raw;
  return null;
}

const C = DOLMIT;

export function ChangePasswordRoute() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [focusField, setFocusField] = useState<"current" | "next" | "confirm" | null>(null);
  const [hover, setHover] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (next === current) {
      setError("New password must differ from your current password.");
      return;
    }
    if (next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    const tokens = loadTokens();
    try {
      const res = await fetch("/api/me/password", {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          ...(tokens?.access ? { authorization: `Bearer ${tokens.access}` } : {}),
        },
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      if (res.status === 200) {
        const body = (await res.json()) as { accessToken: string; refreshToken: string };
        saveTokens({ access: body.accessToken, refresh: body.refreshToken });
        const from = readFromParam();
        // `href:` is the untyped escape hatch — `from` is already same-origin
        // path-validated by readFromParam().
        await navigate({ href: from ?? "/" });
        return;
      }
      if (res.status === 401) {
        // Differentiate "wrong currentPassword" from "expired session": both
        // come back 401, but only `invalid_credentials` means the typed
        // password was wrong. Read the body once as text and parse safely
        // — empty / non-JSON bodies fall through to "session expired".
        let code: string | undefined;
        try {
          const text = await res.text();
          if (text) code = (JSON.parse(text) as { code?: string }).code;
        } catch {
          // non-JSON body — leave code undefined, treat as session-expired.
        }
        setError(
          code === "invalid_credentials"
            ? "Current password is incorrect."
            : "Your session expired. Please sign in again.",
        );
        return;
      }
      if (res.status === 422) {
        setError("That password isn't allowed. Choose a different one.");
        return;
      }
      setError("Could not change password. Please try again.");
    } catch (_err) {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background: C.canvas,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "100px 24px 24px",
        fontFamily: "'Source Sans Pro',system-ui,sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: C.surface,
          borderRadius: 12,
          border: `1px solid ${C.line}`,
          boxShadow: C.shM,
          padding: "32px 32px 28px",
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <BrandHeader size="lg" />
        </div>
        <h1
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: C.navyDeep,
            margin: "0 0 4px",
            fontFamily: "'Poppins',system-ui,sans-serif",
            textAlign: "center",
          }}
        >
          Change password
        </h1>
        <p
          style={{
            fontSize: 12.5,
            color: C.slateMut,
            margin: "0 0 22px",
            textAlign: "center",
          }}
        >
          Set a new password for your account.
        </p>
        <form onSubmit={onSubmit} noValidate>
          <Field label="Current password">
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              onFocus={() => setFocusField("current")}
              onBlur={() => setFocusField(null)}
              style={inputStyle(focusField === "current")}
            />
          </Field>
          <Field label="New password (min 8 chars)">
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              onFocus={() => setFocusField("next")}
              onBlur={() => setFocusField(null)}
              style={inputStyle(focusField === "next")}
            />
          </Field>
          <Field label="Confirm new password">
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onFocus={() => setFocusField("confirm")}
              onBlur={() => setFocusField(null)}
              style={inputStyle(focusField === "confirm")}
            />
          </Field>
          {error && (
            <div
              role="alert"
              style={{
                fontSize: 12.5,
                color: C.r,
                background: C.rBg,
                border: `1px solid ${C.rBd}`,
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 14,
              }}
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: 6,
              border: `1px solid ${hover && !busy ? C.blueHover : C.blue}`,
              background: busy ? C.blueTint5 : hover ? C.blueHover : C.blue,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.7 : 1,
              transition: "all .12s",
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          >
            {busy ? "Saving…" : "Change password"}
          </button>
        </form>
      </div>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 600,
          color: C.slateMut,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 5,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function inputStyle(focused: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: "8px 11px",
    borderRadius: 6,
    border: `1px solid ${focused ? C.blue : C.line}`,
    outline: "none",
    fontSize: 13,
    color: C.navy,
    background: C.surface,
    boxSizing: "border-box",
    fontFamily: "'Source Sans Pro',system-ui,sans-serif",
    transition: "border-color .12s",
  };
}
